import { Button, Text } from '@cretesuite/design-system'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonTextarea,
  IonTitle,
  IonToolbar,
} from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { useEffect, useState } from 'react'
import { useMutation } from 'urql'
import type { GeolocationInput } from '../../gql/graphql'
import { ClockOutMutation } from '../../graphql/timeclock'
import { submitQueueable } from '../../helpers/submitQueueable'
import { useTimeClock } from '../../hooks/timeclock/useTimeClock'
import { useTimeClockTasks } from '../../hooks/timeclock/useTimeClockTasks'
import { useNetwork } from '../../hooks/useNetwork'
import { useToast } from '../../hooks/useToast'
import { useGeoLocation } from './hooks/useGeolocation'

type ClockOutModalProps = {
  onDismiss: () => void
}

export const ClockOutModal = ({ onDismiss }: ClockOutModalProps) => {
  const { _ } = useLingui()
  const { isOnline } = useNetwork()
  const [presentToast] = useToast()
  const { data: currentTimeClock } = useTimeClock()
  const { tasks, loading: tasksLoading } = useTimeClockTasks()
  const [, clockOut] = useMutation(ClockOutMutation)
  const { getCurrentCoords, preFetchGeolocation } = useGeoLocation({
    actionText: 'clock out',
  })

  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const currentTaskId = currentTimeClock?.task?.id
  const isGeoRequired =
    tasks.find((t) => t.id === currentTaskId)?.isGeoRequired ?? false
  // The current task's geo requirement is unknown until the task list loads, so
  // block submit while it's pending (only when there's an active task) to avoid
  // clocking out without the required location.
  const tasksNotReady = !!currentTaskId && tasksLoading

  useEffect(() => {
    if (!isGeoRequired) return
    preFetchGeolocation()
  }, [isGeoRequired, preFetchGeolocation])

  const handleSubmit = async () => {
    setError(null)
    setSubmitting(true)

    try {
      let geolocation: GeolocationInput | undefined
      if (isGeoRequired) {
        const coords = await getCurrentCoords()
        if (!coords) return
        geolocation = coords
      }

      const exec = clockOut({
        data: {
          note: note || undefined,
          geolocation,
        },
      })

      // Never block on the network — the optimistic update already reflects the
      // clock-out. Offline or a too-slow connection → queue and sync in the
      // background; a responsive connection still surfaces real server errors.
      const outcome = await submitQueueable(exec, { isOnline })
      if (outcome.status === 'error') {
        setError(outcome.result.error?.message ?? _(msg`An error occurred`))
        return
      }

      presentToast({
        color: 'success',
        message:
          outcome.status === 'queued'
            ? _(msg`Saved. It will sync when you're back online.`)
            : _(msg`You have been clocked out.`),
        duration: 3000,
      })
      onDismiss()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : _(msg`An error occurred`))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <IonHeader className='ion-no-border'>
        <IonToolbar>
          <IonTitle>{_(msg`Clock Out`)}</IonTitle>
          <IonButtons slot='end'>
            <IonButton onClick={onDismiss}>{_(msg`Close`)}</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <IonList>
          <IonItem>
            <IonLabel position='stacked'>{_(msg`Comments`)}</IonLabel>
            <IonTextarea
              value={note}
              onIonInput={(e) => setNote(e.detail.value ?? '')}
              placeholder={_(msg`Comments related to clocking out`)}
              autoGrow
            />
          </IonItem>
        </IonList>

        {error && (
          <div className='ion-padding'>
            <Text variant='label' className='text-destructive'>
              {error}
            </Text>
          </div>
        )}

        <div className='ion-text-center ion-padding'>
          <Button
            onClick={handleSubmit}
            disabled={submitting || tasksNotReady}
            variant='destructive'
          >
            {submitting ? _(msg`Submitting...`) : _(msg`Clock Out`)}
          </Button>
        </div>
      </IonContent>
    </>
  )
}
